import { useState } from 'react';
import { EngineeringNav } from '@/components/EngineeringNav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Send, Mail, MapPin, CheckCircle, Paperclip, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EngineeringContact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [attachment, setAttachment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('File size must be less than 10MB');
        return;
      }
      setAttachment(file);
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    try {
      // For now, we'll submit the form data without file upload to backend
      // File would need to be handled separately with multipart form data
      await axios.post(`${API}/contacts`, {
        ...formData,
        attachment_name: attachment?.name || null
      });
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-black" data-testid="contact-success">
        <EngineeringNav />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <div className="w-20 h-20 bg-[#F54021]/20 border-2 border-[#F54021] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#F54021]" />
          </div>
          <h1 className="font-nunito text-3xl font-bold text-white mb-4">
            Message Sent!
          </h1>
          <p className="font-nunito text-gray-400 mb-8">
            Thank you for reaching out. We'll get back to you as soon as possible.
          </p>
          <Button 
            onClick={() => {
              setIsSubmitted(false);
              setFormData({ name: '', email: '', message: '' });
              setAttachment(null);
            }}
            className="bg-[#F54021] hover:bg-[#ff5a35] font-nunito font-bold rounded-full"
            data-testid="send-another-btn"
          >
            Send Another Message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="engineering-contact">
      <EngineeringNav />
      
      {/* Hero */}
      <section className="bg-[#0a0a0a] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="font-nunito text-sm text-[#F54021] mb-4 tracking-wider uppercase font-bold">Contact Us</p>
            <h1 className="font-nunito text-4xl lg:text-5xl font-extrabold text-white mb-6">
              Get in Touch
            </h1>
            <p className="font-nunito text-lg text-gray-400">
              Have a question or want to discuss a project? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="font-nunito text-2xl font-bold text-white mb-6">Send a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="font-nunito font-semibold text-white">
                    Name <span className="text-[#F54021]">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    className="bg-[#1a1a1a] border-[#333333] text-white focus:border-[#F54021] focus:ring-[#F54021] font-nunito"
                    data-testid="contact-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-nunito font-semibold text-white">
                    Email <span className="text-[#F54021]">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="bg-[#1a1a1a] border-[#333333] text-white focus:border-[#F54021] focus:ring-[#F54021] font-nunito"
                    data-testid="contact-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="font-nunito font-semibold text-white">
                    Message <span className="text-[#F54021]">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="How can we help you?"
                    rows={5}
                    className="bg-[#1a1a1a] border-[#333333] text-white focus:border-[#F54021] focus:ring-[#F54021] resize-none font-nunito"
                    data-testid="contact-message-input"
                  />
                </div>

                {/* File Attachment */}
                <div className="space-y-2">
                  <Label className="font-nunito font-semibold text-white">
                    Attachment (Optional)
                  </Label>
                  {attachment ? (
                    <div className="flex items-center gap-3 p-3 bg-[#1a1a1a] border border-[#333333] rounded-lg">
                      <Paperclip className="w-5 h-5 text-[#F54021]" />
                      <span className="font-nunito text-sm text-gray-300 flex-1 truncate">{attachment.name}</span>
                      <button 
                        type="button"
                        onClick={removeAttachment}
                        className="p-1 hover:bg-[#333333] rounded"
                      >
                        <X className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center gap-3 p-3 bg-[#1a1a1a] border border-[#333333] border-dashed rounded-lg cursor-pointer hover:border-[#F54021] transition-colors">
                      <Paperclip className="w-5 h-5 text-gray-500" />
                      <span className="font-nunito text-sm text-gray-500">Click to attach a file (max 10MB)</span>
                      <input 
                        type="file" 
                        className="hidden" 
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.dxf,.dwg,.step,.stp,.stl"
                        data-testid="contact-file-input"
                      />
                    </label>
                  )}
                  <p className="font-nunito text-xs text-gray-500">
                    Supported: PDF, DOC, Images, DXF, DWG, STEP, STL
                  </p>
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full md:w-auto px-8 py-3 bg-[#F54021] hover:bg-[#ff5a35] font-nunito font-bold rounded-full"
                  data-testid="submit-contact-btn"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="font-nunito text-2xl font-bold text-white mb-6">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#F54021]/20 border border-[#F54021] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#F54021]" />
                  </div>
                  <div>
                    <h3 className="font-nunito font-bold text-white">Email</h3>
                    <a 
                      href="mailto:Ben@BJengineering.uk" 
                      className="font-nunito text-[#F54021] hover:underline"
                    >
                      Ben@BJengineering.uk
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#F54021]/20 border border-[#F54021] flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-[#F54021]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-nunito font-bold text-white">Phone</h3>
                    <a 
                      href="tel:07928056132" 
                      className="font-nunito text-[#F54021] hover:underline"
                    >
                      07928 056132
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#F54021]/20 border border-[#F54021] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#F54021]" />
                  </div>
                  <div>
                    <h3 className="font-nunito font-bold text-white">Location</h3>
                    <a 
                      href="https://www.google.com/maps/place/Monmouth,+UK" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-nunito text-[#F54021] hover:underline"
                    >
                      Monmouth, UK
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-[#333333] py-12 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-nunito text-sm text-gray-500 text-center">
            © 2024 BJ Engineering. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
